import { createQuery } from "@tanstack/solid-query";
import { fetchBookingDetail, fetchRecentBookings } from "@modules/bookings/service";
import type { BookingSummary } from "@modules/bookings/types";
import { queryKeys } from "@core/query";
import type { PaymentDetail, PaymentSummary } from "./types";

const mapPaymentSummary = (booking: BookingSummary): PaymentSummary => ({
  id: booking.id,
  bookingId: booking.id,
  userId: booking.userId,
  status: booking.status,
  paymentIntentId: booking.paymentIntentId,
  amount: booking.amount,
  currency: booking.currency,
  createdAt: booking.createdAt,
  updatedAt: booking.updatedAt,
});

export const fetchRecentPayments = async (take = 40): Promise<PaymentSummary[]> => {
  const bookings = await fetchRecentBookings(take);
  return bookings.map(mapPaymentSummary);
};

export const fetchPaymentDetail = async (paymentId: string): Promise<PaymentDetail | null> => {
  const bookingDetail = await fetchBookingDetail(paymentId);
  if (!bookingDetail) {
    return null;
  }

  return {
    payment: mapPaymentSummary(bookingDetail.summary),
    booking: bookingDetail.summary,
    statusHistory: bookingDetail.statusHistory,
  };
};

export const usePaymentListQuery = () =>
  createQuery(() => ({
    queryKey: queryKeys.payments.root(),
    queryFn: () => fetchRecentPayments(),
  }));

export const usePaymentDetailQuery = (paymentId: () => string | null) =>
  createQuery(() => {
    const id = paymentId();
    return {
      queryKey: queryKeys.payments.detail(id ?? "detail"),
      enabled: Boolean(id),
      queryFn: () => (id ? fetchPaymentDetail(id) : Promise.resolve(null)),
    };
  });
